// src/services/WebSocketProvider.js
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const websocketRef = useRef(null);
  const [messageQueue, setMessageQueue] = useState([]);
  const reconnectionAttempts = useRef(0);
  const connectionPromise = useRef(null);

  const initializeWebSocket = (accountId, userId) => {
    console.log('initializeWebSocket called with:', accountId, userId);
    if (websocketRef.current) {
      if (websocketRef.current.readyState === WebSocket.OPEN || websocketRef.current.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket already connected or connecting');
        return;
      }
    }
    connectWebSocket(accountId, userId);
  };

  const closeWebSocket = () => {
    console.log('closeWebSocket called');
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
      setConnection(null);
      setIsConnected(false);
    }
  };

  const waitForConnection = () => {
    if (isConnected && websocketRef.current?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    
    if (!connectionPromise.current) {
      connectionPromise.current = {};
      connectionPromise.current.promise = new Promise((resolve, reject) => {
        connectionPromise.current.resolve = resolve;
        connectionPromise.current.reject = reject;
        
        // Timeout after 10 seconds
        setTimeout(() => {
          if (connectionPromise.current) {
            connectionPromise.current.reject(new Error('WebSocket connection timeout'));
            connectionPromise.current = null;
          }
        }, 10000);
      });
    }
    
    return connectionPromise.current.promise;
  };

  const connectWebSocket = useCallback((accountId, userId) => {
    console.log('connectWebSocket called with:', accountId, userId);
    if (!accountId || !userId) {
      console.log('Missing accountId or userId, cannot connect');
      return;
    }

    const wsUrl = `${process.env.REACT_APP_WEBSOCKET_URL || 'wss://f4ws1u4qh4.execute-api.us-east-1.amazonaws.com/dev'}?account_id=${accountId}&user_id=${userId}`;
    console.log('Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebSocket connected successfully');
      reconnectionAttempts.current = 0;
      setConnection(ws);
      setIsConnected(true);
      if (connectionPromise.current) {
        connectionPromise.current.resolve();
        connectionPromise.current = null;
      }
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('WebSocket message received:', message);
      setMessageQueue(prevQueue => [...prevQueue, message]);
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      setConnection(null);
      setIsConnected(false);
      if (reconnectionAttempts.current < 5) {
        const delay = Math.min(30000, 1000 * 2 ** reconnectionAttempts.current);
        console.log(`Reconnecting in ${delay}ms (attempt ${reconnectionAttempts.current + 1})`);
        setTimeout(() => {
          reconnectionAttempts.current += 1;
          connectWebSocket(accountId, userId);
        }, delay);
      } else {
        console.error('Max reconnection attempts reached.');
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
      if (connectionPromise.current) {
        connectionPromise.current.reject(error);
        connectionPromise.current = null;
      }
    };

    websocketRef.current = ws;
  }, []);

  useEffect(() => {
    return () => {
      closeWebSocket();
    };
  }, [connectWebSocket]);

  return (
    <WebSocketContext.Provider value={{ connection, isConnected, initializeWebSocket, closeWebSocket, messageQueue, setMessageQueue, waitForConnection }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);